import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { Shield, ShieldCheck, CheckCircle, Send, ThumbsUp, ThumbsDown, Sparkles, HelpCircle } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const products = [
  {
    id: "avast",
    name: "Avast",
    description: "Antivirus, VPN, Cleanup",
    icon: Shield,
    colorClass: "avast"
  },
  {
    id: "avg",
    name: "AVG",
    description: "AntiVirus, TuneUp, VPN",
    icon: ShieldCheck,
    colorClass: "avg"
  },
  {
    id: "norton",
    name: "Norton",
    description: "360, Family, VPN",
    icon: CheckCircle,
    colorClass: "norton"
  }
];

export default function App() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [question, setQuestion] = useState("");
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const handleProductSelect = (productId) => {
    setSelectedProduct(productId);
    // Reset response when changing product
    if (response) {
      setResponse(null);
      setFeedbackSubmitted(false);
      setSelectedFeedback(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedProduct || !question.trim()) {
      toast.error("Veuillez sélectionner un produit et poser une question");
      return;
    }

    setIsLoading(true);
    setResponse(null);
    setFeedbackSubmitted(false);
    setSelectedFeedback(null);

    try {
      const res = await axios.post(`${API}/ask`, {
        product: selectedProduct,
        question: question.trim()
      });
      setResponse(res.data);
      toast.success("Réponse générée avec succès !");
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la génération de la réponse");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedback = async (isHelpful) => {
    if (!response || feedbackSubmitted) return;

    setSelectedFeedback(isHelpful);

    try {
      await axios.post(`${API}/feedback`, {
        question_id: response.id,
        is_helpful: isHelpful
      });
      setFeedbackSubmitted(true);
      toast.success("Merci pour votre retour !");
    } catch (error) {
      console.error("Feedback error:", error);
      toast.error("Erreur lors de l'envoi du feedback");
      setSelectedFeedback(null);
    }
  };

  const canSubmit = selectedProduct && question.trim() && !isLoading;

  return (
    <div className="cyber-app" data-testid="cyber-support-app">
      <Toaster 
        position="top-right" 
        richColors 
        toastOptions={{
          style: {
            background: '#18181B',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff'
          }
        }}
      />
      
      {/* Header */}
      <header className="cyber-header">
        <div className="header-content">
          <div className="logo-icon">
            <Sparkles size={20} />
          </div>
          <h1 className="header-title">CyberSupport AI</h1>
          <span className="header-subtitle">Support technique intelligent</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="cyber-main">
        {/* Hero Section */}
        <section className="hero-section">
          <h2 className="hero-title">
            Besoin d'aide avec votre <span className="highlight">antivirus</span> ?
          </h2>
          <p className="hero-description">
            Sélectionnez votre produit et posez votre question. Notre assistant IA vous fournira une réponse personnalisée en quelques secondes.
          </p>
        </section>

        {/* Product Selector */}
        <section className="product-section">
          <p className="section-label">1. Choisissez votre produit</p>
          <div className="product-grid">
            {products.map((product) => {
              const IconComponent = product.icon;
              return (
                <div
                  key={product.id}
                  data-testid={`product-card-${product.id}`}
                  className={`product-card ${selectedProduct === product.id ? 'selected' : ''}`}
                  onClick={() => handleProductSelect(product.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleProductSelect(product.id)}
                  aria-pressed={selectedProduct === product.id}
                >
                  <div className={`product-icon ${product.colorClass}`}>
                    <IconComponent size={24} />
                  </div>
                  <span className="product-name">{product.name}</span>
                  <span className="product-description">{product.description}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Question Section */}
        <section className="question-section">
          <p className="section-label">2. Posez votre question</p>
          <div className="question-container">
            <textarea
              data-testid="question-input"
              className="question-textarea"
              placeholder="Décrivez votre problème ou posez votre question ici... Par exemple: 'Comment installer Avast sur Windows 11 ?' ou 'Mon antivirus ne se met plus à jour'"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              disabled={isLoading}
            />
            <button
              data-testid="submit-button"
              className={`submit-button ${isLoading ? 'loading' : ''}`}
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {isLoading ? (
                <>
                  <div className="spinner" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Obtenir une réponse
                </>
              )}
            </button>
          </div>
        </section>

        {/* Response Section */}
        {response && (
          <section className="response-section" data-testid="response-section">
            <div className="response-card">
              <div className="response-header">
                <div className="response-icon">
                  <HelpCircle size={16} />
                </div>
                <span className="response-title">Réponse de CyberSupport AI</span>
              </div>
              <div className="response-content" data-testid="response-content">
                {response.answer.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
              <div className="feedback-section">
                <span className="feedback-label">Cette réponse vous a-t-elle aidé ?</span>
                <div className="feedback-buttons">
                  <button
                    data-testid="feedback-helpful"
                    className={`feedback-btn helpful ${selectedFeedback === true ? 'selected' : ''}`}
                    onClick={() => handleFeedback(true)}
                    disabled={feedbackSubmitted}
                    aria-label="Réponse utile"
                  >
                    <ThumbsUp size={16} />
                    Oui
                  </button>
                  <button
                    data-testid="feedback-unhelpful"
                    className={`feedback-btn unhelpful ${selectedFeedback === false ? 'selected' : ''}`}
                    onClick={() => handleFeedback(false)}
                    disabled={feedbackSubmitted}
                    aria-label="Réponse pas utile"
                  >
                    <ThumbsDown size={16} />
                    Non
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="cyber-footer">
        <p>CyberSupport AI - Assistant technique pour Avast, AVG et Norton</p>
      </footer>
    </div>
  );
}
